import { UUID } from "crypto";
import { Dispatch, SetStateAction, useState } from "react";

import useStudyService from "./useStudyService";
import { AudioAssignment, FileEditState, FileType, SaveStudy } from "./manageStudyModels";
import { Study } from "../models";

type StudyEditContext = {
  study_guid?: UUID;

  // Contains the current edit state of the Study
  study: SaveStudy;

  study_status?: string;

  // Contains the DOM File references and sufficient metadata to identify each file
  files: FileEditState[];

  isLoading: boolean;
};

export function useEditStudyContext(study_guid?: UUID) {
  const StudyService = useStudyService();

  const [state, setState] = useState<StudyEditContext>({
    study_guid,
    study: {},
    files: [],
    isLoading: false,
  });

  const [serialized, setSerialized] = useState<string | undefined>();

  // -- Internal Computed State -----------------------------------------------
  const study_previous = serialized ? (JSON.parse(serialized) as SaveStudy) : undefined;
  const hasAudioChanges =
    study_previous && JSON.stringify(study_previous.audio_assignments) != JSON.stringify(state.study.audio_assignments);
  const hasSummaryChange = study_previous?.screener_summary_guid != state.study.screener_summary_guid;

  const hasChanges = serialized != JSON.stringify(state.study);
  const uploadInProgress = state.files.filter((_) => _.uploadProgress !== undefined).length > 0;
  const isLoading = state.isLoading || uploadInProgress;
  const hasAnyAudioFiles = state.files.filter((_) => _.file_type == FileType.AUDIO && _.s3_file_location).length > 0;

  // -- General Context Methods -----------------------------------------------
  const setStudy: Dispatch<SetStateAction<SaveStudy>> = (value: SetStateAction<SaveStudy>) => {
    if (typeof value === "function") {
      setState((_state) => ({ ..._state, study: value(_state.study!) }));
    } else {
      setState((_state) => ({ ..._state, study: value }));
    }
  };

  const syncStudyModel = (metadata_study: Study) => {
    const edit_model = StudyService.editModelFromMetadata(metadata_study);
    const edit_files = StudyService.editFilesFromMetadata(metadata_study);
    const study_status = metadata_study.study_status.toLowerCase();

    setState((_) => ({ ..._, study: edit_model, files: edit_files, study_status }));

    // Only write the serialized model on sync so we have a baseline to compare to to determine if changes have been applied
    setSerialized(JSON.stringify(edit_model));
  };

  const toggleLoading = (isLoading?: boolean) => {
    setState((_) => ({ ..._, isLoading: isLoading != undefined ? isLoading : !_.isLoading }));
  };

  const guideFileTypes = [
    FileType.SCREENER_GUIDE,
    FileType.SCREENER_SUMMARY,
    FileType.DISCUSSION_GUIDE,
    FileType.POWERPOINT,
  ];

  const setGuideFile = (file_type: FileType, file?: File, uploaded_file_guid?: UUID) => {
    if (!guideFileTypes.includes(file_type)) {
      throw `setGuideFile(): ${file_type} is not a valid Guide Type`;
    }

    setState((_) => {
      const next = { ..._, files: [..._.files] };

      // -- Update the FileEditState reference
      const guideFile = next.files.find((_) => _.file_type == file_type);
      if (guideFile) {
        // Update the guide file instance if exists
        guideFile.file = file;
        guideFile.uploaded_file_guid = uploaded_file_guid;

        // If the call is unsetting the GUID, assume we are trying to remove the existing file and reset all the relevant properties
        if (!uploaded_file_guid) {
          guideFile.original_name = undefined;
          guideFile.process_status = undefined;
          guideFile.s3_file_location = undefined;
        }
      } else {
        // Otherwise create and add a new one to the edit context
        next.files = [...next.files, { file_type, uploaded_file_guid, file }];
      }

      // -- Update the EditStudy model GUID reference
      next.study = {
        [FileType.SCREENER_GUIDE]: (_study: SaveStudy, _guid?: UUID) => {
          return { ..._study, screener_guide_guid: _guid };
        },
        [FileType.SCREENER_SUMMARY]: (_study: SaveStudy, _guid?: UUID) => {
          return { ..._study, screener_summary_guid: _guid };
        },
        [FileType.DISCUSSION_GUIDE]: (_study: SaveStudy, _guid?: UUID) => {
          return { ..._study, discussion_guide_guid: _guid };
        },
        [FileType.POWERPOINT]: (_study: SaveStudy, _guid?: UUID) => {
          return { ..._study, power_point_guid: _guid };
        },
      }[file_type](next.study, uploaded_file_guid);

      return next;
    });
  };

  /** Checks the internal files selection to see if the user is attempting to upload a file they already selected */
  const uploadFileExists = (file: File) => {
    return state.files.filter((_) => _.file?.name == file.name || _.original_name == file.name).length > 0;
  };

  const sanitizeString = (str: string) => {
    return str.replace(/[- *]/g, "").toLowerCase();
  };

  const getGuideFile = (file_type: FileType) => {
    if (!guideFileTypes.includes(file_type)) {
      throw `getGuideFile(): ${file_type} is not a valid Guide Type`;
    }

    return state.files.find((_) => _.file_type == file_type);
  };

  const setAudioFile = (audio_file_id: string, file?: File, uploaded_file_guid?: UUID) => {
    setState((_) => {
      const next = { ..._, files: [..._.files] };

      // Update the internal file array that maintains UI file state
      const audioFile = next.files.find((_) => _.audio_file_id == audio_file_id);
      if (audioFile) {
        audioFile.file = file;
        audioFile.uploaded_file_guid = uploaded_file_guid;

        // If the call is unsetting the GUID, assume we are trying to remove the existing file and reset all the relevant properties
        if (!uploaded_file_guid) {
          audioFile.original_name = undefined;
          audioFile.process_status = undefined;
          audioFile.s3_file_location = undefined;
        }
      } else {
        next.files = [...next.files, { file_type: FileType.AUDIO, audio_file_id, uploaded_file_guid, file }];
      }

      // Update the EditModel that gets submitted to the API on Save
      const assignment = next.study.audio_assignments?.find((_) => _.audio_file_id == audio_file_id);
      if (assignment) {
        assignment.deepsense_file_guid = uploaded_file_guid;
      }

      return next;
    });
  };

  const getAudioFile = (audio_file_id?: string) => {
    return state.files.find((_) => _.audio_file_id == audio_file_id);
  };

  const save = async () => {
    if (!canSave) return;

    let saved_study: Study | undefined;
    toggleLoading(true);
    try {
      const options = {
        transcribe: hasAudioChanges || hasSummaryChange,
      };
      saved_study = await StudyService.saveStudy(state.study!, state.study_guid!, options);
    } finally {
      toggleLoading(false);
    }

    // Save Study returns the Metadata model which we can re-apply to our local state to verify were in sync
    syncStudyModel(saved_study);

    return saved_study;
  };

  const setUploadProgress = (fileEditStateReference: FileEditState, uploadProgress?: number) => {
    setState((_) => {
      const _next = { ..._, files: [..._.files] };

      const fileEditState = _.files.find((_fileEditState) => _fileEditState === fileEditStateReference);
      if (fileEditState) {
        fileEditState.uploadProgress = uploadProgress;
      }

      return _next;
    });
  };

  // For an uploaded SCREENER_SUMMARY, attempt to map the result into the current edit context while keeping any details currently applied to the context
  const processScreenerSummary = async (uploaded_file_guid: UUID) => {
    // ex: {
    //   audio_respondents = [
    //     { "audio_file_id": "651", "respondent_ids": ["484"] },
    //     { "audio_file_id": "501", "respondent_ids": ["223"] },
    //     { "audio_file_id": "755", "respondent_ids": ["805"] },
    //   ],
    //   error_messages: []
    // }
    const response = await StudyService.verifyScreenerSummary(uploaded_file_guid);

    setState((_) => {
      // Retain the current list of audio files
      const currentAudioFiles = _.files.filter((_) => _.file_type == FileType.AUDIO);
      const currentAssociatedAudioFiles = [...(_.study.audio_assignments ?? [])];

      // Create the next state without any audio files
      const _next = {
        ..._,
        files: [..._.files.filter((_) => _.file_type != FileType.AUDIO)],
        study: { ..._.study, audio_assignments: [] as AudioAssignment[] },
      };

      // Map in each new respondent entity, copying over any details from the existing audio files
      response.audio_respondents.forEach((_assignment) => {
        // >> UI File State Management Logic
        const _audio_file = currentAudioFiles.find((_) => _.audio_file_id == _assignment.audio_file_id);
        if (_audio_file) {
          // If exists in the current files, create a copy and add it to the edit context
          _next.files.push({ ..._audio_file });
        } else {
          // Otherwise generate a blank entry with audio_file_id set
          _next.files.push({
            file_type: FileType.AUDIO,
            audio_file_id: _assignment.audio_file_id,
          });
        }

        // >> Edit Model File Association Logic
        const _audio_asc = currentAssociatedAudioFiles.find((_) => _.audio_file_id == _assignment.audio_file_id);
        if (_audio_asc) {
          // Push the current association to the StudyEdit model is one already exists
          _next.study.audio_assignments.push({ ..._audio_asc });
        } else {
          // Add a new entry to the association list
          _next.study.audio_assignments.push({ ..._assignment }); // _assignment IS a AudioAssignment
        }
      });

      return _next;
    });
  };

  const canSave =
    !!state.study_guid &&
    !isLoading &&
    hasChanges &&
    // A SCREENER_SUMMARY must be present as indicated by having a GUID set, but a s3_file_location is not required because that only gets established on save
    (getGuideFile(FileType.SCREENER_SUMMARY)?.uploaded_file_guid ||
      getGuideFile(FileType.POWERPOINT)?.uploaded_file_guid);

  const canPublish =
    !hasChanges &&
    !isLoading &&
    (hasAnyAudioFiles || getGuideFile(FileType.POWERPOINT)?.s3_file_location) &&
    state.study_status != "published" &&
    ((getGuideFile(FileType.SCREENER_SUMMARY)?.s3_file_location &&
      getGuideFile(FileType.DISCUSSION_GUIDE)?.s3_file_location) ||
      getGuideFile(FileType.POWERPOINT)?.s3_file_location);

  return {
    study: state.study,
    setStudy,

    getGuideFile,
    setGuideFile,
    getAudioFile,
    setAudioFile,
    processScreenerSummary,

    setUploadProgress,

    isPowerPoint: !!state.study.power_point_guid,

    syncStudyModel,
    save,

    uploadFileExists,
    sanitizeString,

    toggleLoading,
    isLoading,

    hasChanges,
    hasSummaryChange,
    uploadInProgress,
    canSave,
    canPublish,
  };
}

export type EditStudyContext = ReturnType<typeof useEditStudyContext>;
